/**
 * 本文件是HttpRequest核心函数模块
 * @author 笑三少
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import router from '@/router'
import { LOGIN_PATH } from '@/config/setting'
import { RequestConfig, responseItem } from '@/api'

// ========== 类型定义 ==========

/** 请求队列项 */
interface QueueItem {
  resolve: (value: any) => void
  reject: (reason?: any) => void
}

/** 扩展的请求配置 */
export interface ExtendedRequestConfig extends AxiosRequestConfig {
  customConfig?: RequestConfig
  _retry?: boolean
}

/** 缓存项 */
interface CacheItem<T = any> {
  data: T
  timestamp: number
  expireTime: number
}

// ========== 常量定义 ==========

const DEFAULT_CONFIG: RequestConfig = {
  loading: true,  //是否显示加载动画
  cancelToken: false, //是否缓存token
  retry: false, //是否重试
  retryCount: 3, //重试次数
  retryDelay: 1000, //重试间隔时间
  cache: false, //是否缓存
  cacheTime: 5 * 60 * 1000  //缓存生存周期
}

const REFRESH_TOKEN_URL = '/refreshToken'
const NO_RETRY_STATUS_CODES = [400, 401, 403, 404, 422, 429, 500]

const statusMessages: Record<number, string> = {
  400: '请求参数错误',
  401: '未授权，请重新登录',
  403: '权限不足',
  404: '请求地址不存在',
  500: '服务器内部错误',
  501: '服务器不支持该请求方法',
  502: '网关错误',
  503: '系统维护中,请稍后再试',
  504: '网关超时'
}

// ========== 创建 axios 实例 ==========
const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded'
  },
  withCredentials: true  // 携带 Cookie（HttpOnly Cookie 会自动携带）
})

// ========== 登录状态检查（使用 sessionStorage） ==========
/**
 * 检查登录状态
 */
const checkLoginStatus = (): boolean => {
  const sessionLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true'
  const userInfo = sessionStorage.getItem('userInfo')
  if (!sessionLoggedIn || !userInfo) return false
  // 检查登录是否过期（7天）
  const loginTime = sessionStorage.getItem('loginTime')
  if (loginTime) {
    const loginTimestamp = parseInt(loginTime)
    const sevenDays = 7 * 24 * 60 * 60 * 1000
    if (Date.now() - loginTimestamp > sevenDays) {
      clearAuthState()
      return false
    }
  }
  return true
}

/**
 * 跳转到登录页面
 */
const redirectToLogin = (): void => {
  clearAuthState()
  router.push(LOGIN_PATH)
}

// ========== Token 刷新相关 ==========

let isRefreshing = false
let failedQueue: QueueItem[] = []

const processQueue = (error: Error | null): void => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error)
    else prom.resolve(null)
  })
  failedQueue = []
}

/**
 * 刷新 Token（依赖后端自动处理 HttpOnly Cookie）
 */
/**
 * 刷新 Token（依赖后端自动处理 HttpOnly Cookie）
 */
const refreshToken = async (): Promise<boolean> => {
  if (!checkLoginStatus()) {
    redirectToLogin()
    throw new Error('未登录')
  }
  try {
    const refreshInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      timeout: 10000,
      withCredentials: true
    })
    const response = await refreshInstance.post<responseItem>(REFRESH_TOKEN_URL)
    if (response.data.status !== 1) {
      redirectToLogin()
      throw new Error(response.data.data || '刷新token失败')
    }
    // 增加延迟确保Cookie被浏览器完全处理
    await new Promise(resolve => setTimeout(resolve, 500))
    // 刷新Token成功后，延长sessionStorage中的登录时间
    const currentLoginTime = sessionStorage.getItem('loginTime')
    if (currentLoginTime) sessionStorage.setItem('loginTime', Date.now().toString())
    return true
  } catch (error) {
    redirectToLogin()
    throw error
  }
}

// ========== 请求队列管理 ==========

const pendingRequest = new Map<string, AbortController>()

const generateReqKey = (config: ExtendedRequestConfig): string => {
  const { method, url, params, data } = config
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&')
}

const addPendingRequest = (config: ExtendedRequestConfig): void => {
  const requestKey = generateReqKey(config)
  if (!pendingRequest.has(requestKey)) {
    const controller = new AbortController()
    config.signal = controller.signal
    pendingRequest.set(requestKey, controller)
  }
}

const removePendingRequest = (config: ExtendedRequestConfig): void => {
  const requestKey = generateReqKey(config)
  if (pendingRequest.has(requestKey)) {
    const controller = pendingRequest.get(requestKey)
    controller?.abort()
    pendingRequest.delete(requestKey)
  }
}

export const clearPendingRequest = (): void => {
  pendingRequest.forEach((controller) => {
    controller.abort()
  })
  pendingRequest.clear()
}

// ========== Loading 管理器 ==========

class LoadingManager {
  private loadingCount: number = 0
  private loadingInstance: any = null
  public startLoading(): void {
    this.loadingCount++
    if (this.loadingCount === 1) this.showLoading()
  }
  public hideLoading(): void {
    this.loadingCount = Math.max(0, this.loadingCount - 1)
    if (this.loadingCount === 0 && this.loadingInstance) {
      setTimeout(() => {
        if (this.loadingCount === 0 && this.loadingInstance) {
          this.loadingInstance.close()
          this.loadingInstance = null
        }
      }, 300)
    }
  }

  private showLoading(): void {
    if (this.loadingInstance) return
    import('element-plus').then(({ ElLoading }) => {
      if (!this.loadingInstance && this.loadingCount > 0) {
        this.loadingInstance = ElLoading.service({
          lock: true,
          text: '加载中...',
          background: 'rgba(0, 0, 0, 0.3)',
          fullscreen: true
        })
      }
    }).catch(() => {
      console.warn('Loading组件未导入')
    })
  }
}

const loadingManager = new LoadingManager()

// ========== 缓存管理器 ==========

class CacheManager {
  private cache = new Map<string, CacheItem>()
  public set<T>(key: string, data: T, cacheTime: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expireTime: cacheTime
    })
    this.clearExpiredCache()
  }
  public get<T>(key: string): T | null {
    const cacheItem = this.cache.get(key) as CacheItem<T> | undefined
    if (!cacheItem) return null
    const now = Date.now()
    if (now - cacheItem.timestamp > cacheItem.expireTime) {
      this.cache.delete(key)
      return null
    }
    return cacheItem.data
  }

  public delete(key: string): void {
    this.cache.delete(key)
  }

  public clear(): void {
    this.cache.clear()
  }

  private clearExpiredCache(): void {
    const now = Date.now()
    this.cache.forEach((item, key) => {
      if (now - item.timestamp > item.expireTime) {
        this.cache.delete(key)
      }
    })
  }
}

const cacheManager = new CacheManager()

// ========== 重试管理器 ==========

class RetryManager {
  private maxRetryCount: number
  private retryDelay: number

  constructor(config: RequestConfig = {}) {
    this.maxRetryCount = config.retryCount || 3
    this.retryDelay = config.retryDelay || 1000
  }

  public async retry<T>(fn: () => Promise<T>, retryCount: number = 0): Promise<T> {
    try {
      return await fn()
    } catch (error: any) {
      if (retryCount >= this.maxRetryCount || !this.shouldRetry(error)) {
        throw error
      }
      await this.delay(retryCount)
      return this.retry(fn, retryCount + 1)
    }
  }

  private shouldRetry(error: any): boolean {
    if (error.response) return !NO_RETRY_STATUS_CODES.includes(error.response.status)
    return !error.response ||
      error.code === 'ECONNABORTED' ||
      error.message?.includes('timeout')
  }

  private delay(retryCount: number): Promise<void> {
    const delay = this.retryDelay * Math.pow(2, retryCount)
    return new Promise(resolve => setTimeout(resolve, delay))
  }
}

// ========== 请求拦截器 ==========

service.interceptors.request.use(
  (config: InternalAxiosRequestConfig & ExtendedRequestConfig) => {
    const customConfig: RequestConfig = {
      ...DEFAULT_CONFIG,
      ...config.customConfig
    }
    if (customConfig.loading !== false) loadingManager.startLoading()
    if (customConfig.cancelToken !== false) addPendingRequest(config)
    // 添加时间戳防止缓存（GET请求）
    if (config.method?.toLowerCase() === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ========== 响应拦截器 ==========

service.interceptors.response.use(
  (response: AxiosResponse<responseItem>) => {
    removePendingRequest(response.config as ExtendedRequestConfig)
    const customConfig = (response.config as ExtendedRequestConfig).customConfig || DEFAULT_CONFIG
    if (customConfig.loading !== false) loadingManager.hideLoading()
    // 缓存处理
    if (customConfig.cache && response.data.status === 1) {
      const cacheKey = generateReqKey(response.config as ExtendedRequestConfig)
      cacheManager.set(cacheKey, response.data, customConfig.cacheTime)
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config as ExtendedRequestConfig
    removePendingRequest(originalRequest)
    const customConfig = originalRequest?.customConfig || DEFAULT_CONFIG
    if (customConfig.loading !== false) loadingManager.hideLoading()
    // ========== 处理 503 系统维护 ==========
    if (error.response?.status === 503) {
      // 不进行任何重试或跳转，直接返回错误
      return Promise.reject({
        status: 503,
        data: '系统维护中，请稍后再试'
      })
    }
    // ========== 处理 401 未授权 ==========
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      if (isRefreshing) {
        // 正在刷新，将请求加入队列
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            return service(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }
      isRefreshing = true
      try {
        await refreshToken()
        processQueue(null)
        // 重试原始请求
        return await service(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as Error)
        return Promise.reject({
          status: 401,
          data: '登录已过期，请重新登录'
        })
      } finally {
        isRefreshing = false
      }
    }
    // ========== 处理其他 HTTP 错误 ==========
    let errorMessage = '请求失败'
    let errorStatus = 0
    if (error.response) {
      const { status } = error.response
      errorStatus = status
      errorMessage = statusMessages[status] || `请求错误 ${status}`
    } else if (error.request) {
      errorMessage = error.code === 'ECONNABORTED'
        ? '请求超时'
        : '网络异常'
    } else errorMessage = error.message || errorMessage
    return Promise.reject({
      status: errorStatus,
      data: errorMessage
    })
  }
)

// ========== 请求类封装 ==========

class RequestUtils {
  private service: AxiosInstance
  constructor() {
    this.service = service
  }

  /**
   * GET 请求
   */
  public async get<T = any>(
    url: string,
    params?: Record<string, any>,
    customConfig?: RequestConfig
  ): Promise<responseItem<T>> {
    return this.request<T>({
      method: 'get',
      url,
      params,
      customConfig
    })
  }

  /**
   * POST 请求
   */
  public async post<T = any>(
    url: string,
    data?: Record<string, any>,
    customConfig?: RequestConfig
  ): Promise<responseItem<T>> {
    return this.request<T>({
      method: 'post',
      url,
      data,
      customConfig
    })
  }

  /**
   * PUT 请求
   */
  public async put<T = any>(
    url: string,
    data?: Record<string, any>,
    customConfig?: RequestConfig
  ): Promise<responseItem<T>> {
    return this.request<T>({
      method: 'put',
      url,
      data,
      customConfig
    })
  }

  /**
   * DELETE 请求
   */
  public async delete<T = any>(
    url: string,
    data?: Record<string, any>,
    customConfig?: RequestConfig
  ): Promise<responseItem<T>> {
    return this.request<T>({
      method: 'delete',
      url,
      data,
      customConfig
    })
  }

  /**
   * 通用请求方法
   */
  public async request<T = any>(config: ExtendedRequestConfig): Promise<responseItem<T>> {
    const customConfig: RequestConfig = {
      ...DEFAULT_CONFIG,
      ...config.customConfig
    }
    // 检查缓存
    if (customConfig.cache && config.method?.toLowerCase() === 'get') {
      const cacheKey = generateReqKey(config)
      const cachedData = cacheManager.get<responseItem<T>>(cacheKey)
      if (cachedData) {
        return cachedData
      }
    }
    const requestConfig: ExtendedRequestConfig = {
      ...config,
      customConfig
    }
    try {
      // 处理重试
      if (customConfig.retry) {
        const retryManager = new RetryManager(customConfig)
        const response = await retryManager.retry(() => this.service(requestConfig))
        if (response && response.data) {
          return {
            status: response.data.status,
            data: response.data.data
          }
        }
        return {
          status: 0,
          data: '响应格式错误' as any
        }
      }
      // 正常请求
      const response = await this.service(requestConfig)
      if (response && response.data) {
        return {
          status: response.data.status,
          data: response.data.data
        }
      }
      return {
        status: 0,
        data: '响应格式错误' as any
      }
    } catch (error: any) {
      if (error && typeof error === 'object' && 'status' in error && 'data' in error) {
        return {
          status: error.status,
          data: error.data as any
        }
      }
      // 其他未处理的错误
      return {
        status: 0,
        data: error.message || '请求失败'
      }
    }
  }

  /**
   * 下载文件
   */
  public async download(
    url: string,
    params?: Record<string, any>,
    filename?: string,
    customConfig?: RequestConfig
  ): Promise<responseItem<{ success: boolean; filename: string }>> {
    const finalFilename = filename || `download_${Date.now()}.xlsx`
    const method = 'post'
    const downloadConfig: ExtendedRequestConfig = {
      method,
      url,
      [method === 'get' ? 'params' : 'data']: params,
      responseType: 'blob',
      customConfig: {
        ...customConfig,
        loading: customConfig?.loading !== false
      }
    }
    try {
      const response = await this.service(downloadConfig)
      // 检查响应类型
      const contentType = response.headers['content-type']
      if (contentType?.includes('application/json')) {
        // 如果是 JSON，说明返回了错误信息
        const reader = new FileReader()
        const errorText = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string)
          reader.readAsText(response.data)
        })
        try {
          const errorData = JSON.parse(errorText)
          return {
            status: 0,
            data: { success: false, filename: '' }
          }
        } catch {
          return {
            status: 0,
            data: { success: false, filename: '' }
          }
        }
      }
      // 处理文件名
      let downloadFilename = finalFilename
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"']+)["']?/i)
        if (match?.[1]) {
          downloadFilename = decodeURIComponent(match[1])
        }
      }
      // 创建下载链接
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = downloadFilename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      }, 100)
      return {
        status: 1,
        data: { success: true, filename: downloadFilename }
      }
    } catch (error: any) {
      return {
        status: 0,
        data: { success: false, filename: '' }
      }
    }
  }

  /**
   * 取消请求
   */
  public cancelRequest(config: ExtendedRequestConfig): void {
    removePendingRequest(config)
  }

  /**
   * 取消所有请求
   */
  public cancelAllRequests(): void {
    clearPendingRequest()
  }

  /**
   * 清除缓存
   */
  public clearCache(): void {
    cacheManager.clear()
  }
}

// ========== 创建实例并导出 ==========

const request = new RequestUtils()
export const axios_get = request.get.bind(request)
export const axios_post = request.post.bind(request)
export const axios_put = request.put.bind(request)
export const axios_del = request.delete.bind(request)
export const axios_download = request.download.bind(request)
export default request

// ========== 页面卸载清理 ==========

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    clearPendingRequest()
  })
}
